import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';
import useWindowSize from '../hooks/useWindowSize';
import EventIcon from '@mui/icons-material/Event';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PaymentsIcon from '@mui/icons-material/Payments';
import BarChartIcon from '@mui/icons-material/BarChart';
import SendIcon from '@mui/icons-material/Send';

function VendeTusEntradas() {
  const { colors } = useTheme();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const [form, setForm] = useState({
    nombre_evento: '',
    recinto: '',
    aforo: '',
    fecha: '',
    ciudad: '',
    telefono: '',
    precio_entrada: '',
    email: '',
  });
  const [enviado, setEnviado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'https://galiticket-production.up.railway.app'}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.email || 'Promotor',
          email: form.email,
          subject: `Nueva solicitud: ${form.nombre_evento}`,
          message: `NUEVA SOLICITUD — VENDE TUS ENTRADAS\n\nNombre del evento: ${form.nombre_evento}\nRecinto: ${form.recinto}\nAforo: ${form.aforo}\nFecha: ${form.fecha}\nCiudad: ${form.ciudad}\nTeléfono de contacto: ${form.telefono}\nPrecio entrada: ${form.precio_entrada}€\nEmail de contacto: ${form.email}`,
        }),
      });
      if (res.ok) setEnviado(true);
      else setError('Error al enviar. Inténtalo de nuevo.');
    } catch {
      setError('Error al enviar. Inténtalo de nuevo.');
    }
    setLoading(false);
  };

  const ventajas = [
    { icon: <EventIcon style={{ fontSize: 28, color: colors.accent }} />, title: 'Gestión completa', desc: 'Creamos y gestionamos tu evento en la plataforma. Solo tienes que preocuparte de organizarlo.' },
    { icon: <QrCodeIcon style={{ fontSize: 28, color: colors.accent }} />, title: 'QR por entrada', desc: 'Cada entrada genera un código QR único validable en tiempo real desde cualquier dispositivo.' },
    { icon: <PaymentsIcon style={{ fontSize: 28, color: colors.accent }} />, title: 'Cobro seguro', desc: 'Pagos procesados con Stripe. El dinero llega directamente a tu cuenta de forma segura.' },
    { icon: <BarChartIcon style={{ fontSize: 28, color: colors.accent }} />, title: 'Reportes en tiempo real', desc: 'Accede a estadísticas de ventas, ingresos y asistencia desde tu panel de promotor.' },
  ];

  const inputStyle = {
    width: '100%',
    backgroundColor: colors.bgTertiary,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    color: colors.text,
    fontSize: '0.9rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.bg }}>
      <Helmet>
        <title>Vende tus entradas con GaliTicket</title>
        <meta name="description" content="¿Organizas un evento? Vende tus entradas con GaliTicket. Gestión completa, QR por entrada, cobro seguro y reportes en tiempo real." />
      </Helmet>
      <Navbar />

      <div style={{ padding: isMobile ? '1.5rem 1rem' : '3rem 2rem', maxWidth: '900px', margin: '0 auto' }}>

        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ color: colors.text, fontSize: isMobile ? '1.8rem' : '2.5rem', fontWeight: 'bold', marginBottom: '0.8rem' }}>
            Vende tus entradas con GaliTicket
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: isMobile ? '0.95rem' : '1.1rem', maxWidth: '600px', margin: '0 auto', lineHeight: 1.6 }}>
            ¿Organizas un evento? Ponemos nuestra plataforma a tu disposición para que vendas tus entradas de forma fácil, segura y profesional.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '1rem', marginBottom: '3rem' }}>
          {ventajas.map((v, i) => (
            <div key={i} style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '1.25rem', textAlign: 'center' }}>
              <div style={{ marginBottom: '0.75rem' }}>{v.icon}</div>
              <p style={{ color: colors.text, fontWeight: 'bold', fontSize: '0.9rem', marginBottom: '0.4rem' }}>{v.title}</p>
              <p style={{ color: colors.textSecondary, fontSize: '0.78rem', lineHeight: 1.5, margin: 0 }}>{v.desc}</p>
            </div>
          ))}
        </div>

        <div style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}`, borderRadius: '16px', padding: isMobile ? '1.5rem' : '2rem', transform: 'translateZ(0)', WebkitBackfaceVisibility: 'hidden' }}>
          <h2 style={{ color: colors.text, fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '0.4rem' }}>Cuéntanos tu evento</h2>
          <p style={{ color: colors.textSecondary, fontSize: '0.85rem', marginBottom: '1.5rem' }}>Rellena el formulario y nos ponemos en contacto contigo en menos de 24h.</p>

          {enviado ? (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <p style={{ color: colors.text, fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.5rem' }}>¡Solicitud enviada!</p>
              <p style={{ color: colors.textSecondary, fontSize: '0.9rem' }}>Nos pondremos en contacto contigo en menos de 24 horas.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ color: colors.textSecondary, fontSize: '0.82rem', display: 'block', marginBottom: '0.4rem' }}>Nombre del evento *</label>
                  <input name="nombre_evento" value={form.nombre_evento} onChange={handleChange} required style={inputStyle} placeholder="Ej: Festival de Verán 2026" />
                </div>
                <div>
                  <label style={{ color: colors.textSecondary, fontSize: '0.82rem', display: 'block', marginBottom: '0.4rem' }}>Recinto *</label>
                  <input name="recinto" value={form.recinto} onChange={handleChange} required style={inputStyle} placeholder="Ej: Amoedo Brunch & Lunch" />
                </div>
                <div>
                  <label style={{ color: colors.textSecondary, fontSize: '0.82rem', display: 'block', marginBottom: '0.4rem' }}>Aforo estimado *</label>
                  <input name="aforo" value={form.aforo} onChange={handleChange} required style={inputStyle} placeholder="Ej: 500 personas" />
                </div>
                <div>
                  <label style={{ color: colors.textSecondary, fontSize: '0.82rem', display: 'block', marginBottom: '0.4rem' }}>Fecha del evento *</label>
                  <input name="fecha" type="date" value={form.fecha} onChange={handleChange} required style={inputStyle} />
                </div>
                <div>
                  <label style={{ color: colors.textSecondary, fontSize: '0.82rem', display: 'block', marginBottom: '0.4rem' }}>Ciudad *</label>
                  <input name="ciudad" value={form.ciudad} onChange={handleChange} required style={inputStyle} placeholder="Ej: Vigo" />
                </div>
                <div>
                  <label style={{ color: colors.textSecondary, fontSize: '0.82rem', display: 'block', marginBottom: '0.4rem' }}>Teléfono de contacto *</label>
                  <input name="telefono" type="tel" value={form.telefono} onChange={handleChange} required style={inputStyle} placeholder="Ej: 600 000 000" />
                </div>
                <div>
                  <label style={{ color: colors.textSecondary, fontSize: '0.82rem', display: 'block', marginBottom: '0.4rem' }}>Precio entrada (€) *</label>
                  <input name="precio_entrada" type="number" min="0" value={form.precio_entrada} onChange={handleChange} required style={inputStyle} placeholder="Ej: 15" />
                </div>
                <div>
                  <label style={{ color: colors.textSecondary, fontSize: '0.82rem', display: 'block', marginBottom: '0.4rem' }}>Correo electrónico *</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} required style={inputStyle} placeholder="Ej: email@ejemplo.com" />
                </div>
              </div>

              {error && <p style={{ color: '#ff4444', fontSize: '0.85rem', margin: 0 }}>{error}</p>}

              <button type="submit" disabled={loading} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', backgroundColor: colors.accent, color: colors.accentText, border: 'none', borderRadius: '25px', padding: '0.9rem', fontSize: '1rem', fontWeight: 'bold', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
                <SendIcon style={{ fontSize: 18 }} />
                {loading ? 'Enviando...' : 'Enviar solicitud'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default VendeTusEntradas;